import { gql } from 'graphql-tag';

/**
 * Panel-level GraphQL SDL: one shared contract between the UXP extension panel
 * and web for panel-local messaging (distinct from host-level SDL). The schema
 * is fixed, and the panel resolver implementation is shared across every UXP
 * host—unlike host-level SDL, where each host or integration supplies its own
 * resolvers for the same contract.
 */
export const sharedUxpSchema = gql`
  type Query {
    getAuthTokens: SharedAuthTokens!
    getBufferedUxpRuntimeErrors: [UxpRuntimeErrorPayload!]!
    getBufferedUxpTrackingEvents: [UxpTrackingEventPayload!]!
    getDvaDebugFlags: DvaDebugFlags!
    getLastViewedPath: LastViewedPath!
    getTheme: String!
    getUxpLocalStorageItem(key: String!): String!
    getZeroClickAuthStatus: String!
    isSignedIn: Boolean!
    uxpHostInfo: UXPHostInfo!
    """
    Frame.io comment ids created from marker upload for an asset. Used by the panel
    to exclude upload-origin comments from comment→marker sync.
    """
    commentIdsFromMarkerUpload(assetId: ID!): [ID!]!
  }

  input OpenInBrowserInput {
    url: String!
    omitLocaleParam: Boolean
  }

  input ShowFileOnDiskInput {
    path: String!
  }

  input SetLastViewedPathInput {
    path: String!
  }

  input SetCommentCreatedFromMarkerInput {
    assetId: ID!
    commentId: ID!
  }

  type SharedAuthTokens {
    accessToken: String
    refreshToken: String
  }

  enum ErrorSeverity {
    warning
    error
    fatal
  }

  type UxpRuntimeErrorPayload {
    id: ID!
    message: String!
    operation: String
    severity: ErrorSeverity!
    # Source module that reported the error
    source: String!
    # Stack trace (sanitized)
    stack: String
    timestamp: String!
  }

  type UxpTrackingEventPayload {
    id: ID!
    event: String!
    payload: String!
    options: String!
  }

  type UXPErrorPayload {
    message: String!
  }

  # @see https://developer.adobe.com/xd/uxp/uxp/reference-js/Modules/uxp/Host%20Information/Host/
  type UXPHostInfo {
    # Host application name (eg, premierepro)
    name: String!
    # Host application version (eg, 25.3.0)
    version: String!
    # Host application build (eg build #48)
    build: String
    # 5 letter locale code (eg, en_US)
    uiLocale: String
    # Version of the UXP extension (eg, 0.0.15)
    pluginVersion: String!
    # Version of the UXP runtime (eg, uxp-8.1.0-local)
    uxpVersion: String!
    # Version of the uxp-messaging schema (eg, 0.0.88)
    schemaVersion: String!
  }

  type DvaDebugFlags {
    # URL override for the webview
    webviewUrl: String
    # Environment override for the webview (0 = production, 1 = staging, 2 = development)
    environment: String
    # Massdriver preview environment
    preview: String
  }

  type LastViewedPath {
    path: String!
  }

  type Mutation {
    openInBrowser(input: OpenInBrowserInput!): Boolean

    """
    false when the host cannot reveal the path (for example missing FileUtils) or the host API reports failure
    """
    showFileOnDisk(input: ShowFileOnDiskInput!): Boolean

    signIn: Boolean!
    signOut: Boolean!
    refreshAccessToken: String!

    signInWithAdobeId: Boolean!

    setUxpLocalStorageItem(key: String!, value: String!): String!
    removeUxpLocalStorageItem(key: String!): Boolean!

    setLastViewedPath(input: SetLastViewedPathInput!): String

    getLocalFolderPath: String!

    createUploadTransferJob(
      batchItemId: ID!
      filePaths: [String!]!
      originalFileNames: [String!]
    ): CreateUploadTransferJobPayload

    startUploadTransferJob(input: StartUploadTransferJobInput!): Boolean!

    """
    Cancels an in-flight upload transfer job. Returns true when the job was found and aborted, false when the host has no record of the job.
    """
    cancelUploadTransferJob(jobId: ID!): Boolean!

    """
    Records a Frame.io comment created from marker upload so sync can exclude it
    before host marker metadata is written.
    """
    setCommentCreatedFromMarker(
      input: SetCommentCreatedFromMarkerInput!
    ): Boolean
  }

  type Subscription {
    feedbackButtonTriggered: String!
    themeChanged: String!
    signInChanged: Boolean!
    connectionStatusChanged: Boolean!
    uxpRuntimeError: UxpRuntimeErrorPayload!
    uxpTrackingEvent: UxpTrackingEventPayload!
    uploadJobEvents: UploadJobEventsPayload!
  }

  union UploadJobEventsPayload =
      UploadJobProgressPayload
    | UploadJobItemProgressPayload

  input TransferJobAssetInput {
    assetId: ID!
    uploadUrls: [String!]!
  }

  input StartUploadTransferJobInput {
    jobId: ID!
    assets: [TransferJobAssetInput!]!
  }

  type CreateUploadTransferJobPayload {
    id: ID!
    name: String!
    assets: [TransferJobAsset!]!
  }

  type TransferJobAsset {
    id: Int!
    filesize: Float
    fileType: String
    name: String
    type: String
  }

  type UploadJobProgressPayload {
    id: ID!
    bytesSent: Float!
    bytesTotal: Float!
    completedItems: Int!
    canceledItems: Int!
    failedItems: Int!
    jobId: ID!
    percentage: Float!
    state: UXPTransferState!
    totalItems: Int!
  }

  type UploadJobItemProgressPayload {
    id: ID!
    jobId: ID!
    assetId: ID!
    bytesSent: Float!
    bytesTotal: Float!
    filePath: String!
    itemId: ID!
    percentage: Float!
    remainingTime: Float!
    state: UXPTransferState!
  }

  enum UXPTransferState {
    pending
    failed
    completed
    uploading
    downloading
    canceled
    partially_completed
  }
`;
